import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Grid,
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
  MenuItem,
} from "@mui/material";
import { useSelector } from "react-redux";
import configServ from "../../services/config";
import Swal from "sweetalert2";
import LoaderBackdrop from "../../Components/Loader/LoaderBackdrop";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import dayjs from "dayjs";

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
const MeetingCard = ({
  onSubmit,
  handleClose,
  editData,
  setEditData,
  isEdit,
  setIsEdit,
  fetchTable,
}) => {
  const { company_id } = useSelector((state) => state.GlobalVariables);
  const today = new Date().toISOString().split("T")[0];

  const [batches, setBatches] = useState([]);
  const [errors, setErrors] = useState({}); // <-- Error State
  const [loading, setLoading] = useState(true);

  const [form, setForm] = useState({
    title: "",
    batch_details_id: "",
    google_meet_url: "",
    date: today,
    email_body: "",
  });

  // =======================
  // 🟦 HELPER VALIDATION FUNCTION
  // =======================
  const validateForm = (form) => {
    let errors = {};

    if (!form.title.trim()) errors.title = "Title is required";
    if (!form.batch_details_id) errors.batch_details_id = "Please select batch";

    if (!form.google_meet_url.trim())
      errors.google_meet_url = "Google Meet URL is required";

    if (!form.date) errors.date = "Date is required";

    if (!form.email_body.trim()) errors.email_body = "Email body is required";

    return errors;
  };

  // Handle field update
  const handleChange = (e) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    });

    // Clear error on typing
    setErrors((prev) => ({ ...prev, [e.target.name]: "" }));
  };

  // Fetch active batches
  const fetchbatches = async () => {
    try {
      setLoading(true);
      const result = await configServ.getActiveBatches({ company_id });

      if (result?.status === true) {
        setBatches(result.data);
      } else {
        setBatches([]);
      }
    } catch (error) {
      console.error("Error fetching batches:", error);
      setBatches([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchbatches();
  }, []);

  // Reset button
  const handleReset = () => {
    setForm({
      title: "",
      batch_details_id: "",
      google_meet_url: "",
      date: today,
      email_body: "",
    });
    setErrors({});
    setEditData(null);
    setIsEdit(false);
  };

  // Submit button
  const handleSubmit = async () => {
    const v = validateForm(form);
    setErrors(v);

    if (Object.keys(v).length > 0) return;

    setLoading(true);

    try {
      const payload = {
        ...form,
        company_id,
      };

      let res;

      // 🔥 EDIT MODE → CALL UPDATE API
      if (isEdit && editData?.id) {
        res = await configServ.updateMeeting(editData.id, payload);
      }
      // 🔥 ADD MODE → CALL STORE API
      else {
        res = await configServ.store(payload);
      }

      if (res?.status) {
        Swal.fire({
          icon: "success",
          title: isEdit ? "Updated!" : "Saved!",
          text: isEdit
            ? "Meeting updated successfully."
            : "Meeting created successfully.",
          timer: 1500,
          showConfirmButton: false,
        });

        handleReset();
        handleClose();
        fetchTable();
        if (onSubmit) onSubmit(form);
      } else {
        Swal.fire("Error", res?.message || "Failed to save meeting", "error");
      }
    } catch (error) {
      console.error("Submit Error:", error);
      Swal.fire("Error", "Something went wrong!", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
  if (editData) {
    setForm({
      title: editData.title || "",
      batch_details_id: editData.batch_details_id || "",
      google_meet_url: editData.google_meet_url || "",
      date: editData.date ? dayjs(editData.date) : dayjs(),
      email_body: editData.email_body || "",
    });
  }
}, [editData]);


  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      style={{ width: "100%", padding: "16px" }}
    >
      <LoaderBackdrop open={loading} />
      <Card elevation={3} sx={{ borderRadius: 3 }}>
        <CardContent>
          {/* <Typography variant="h6" fontWeight={700} mb={2}>
            Create / Update Meeting
          </Typography> */}

          <Grid container spacing={2}>
            {/* Title */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Title"
                name="title"
                value={form.title}
                onChange={handleChange}
                fullWidth
                size="small"
                error={!!errors.title}
                helperText={errors.title}
              />
            </Grid>

            {/* Batch Dropdown */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                label="Select Batch"
                name="batch_details_id"
                value={form.batch_details_id}
                onChange={handleChange}
                fullWidth
                size="small"
                error={!!errors.batch_details_id}
                helperText={errors.batch_details_id}
              >
                {batches.map((batch) => (
                  <MenuItem key={batch.id} value={batch.id}>
                    {batch.item_name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Google Meet URL */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Google Meet URL"
                name="google_meet_url"
                value={form.google_meet_url}
                onChange={handleChange}
                fullWidth
                size="small"
                error={!!errors.google_meet_url}
                helperText={errors.google_meet_url}
              />
            </Grid>

            {/* Date */}
            <Grid item xs={12} sm={6} md={3}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateTimePicker
                  label="Select Date & Time"
                  value={form.date}
                  onChange={(newValue) =>
                    handleChange({ target: { name: "date", value: newValue } })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      fullWidth
                      error={!!errors.date}
                      helperText={errors.date}
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>

            {/* Email Body */}
            <Grid item xs={12}>
              <TextField
                label="Email Body"
                name="email_body"
                value={form.email_body}
                onChange={handleChange}
                fullWidth
                multiline
                minRows={3}
                size="small"
                error={!!errors.email_body}
                helperText={errors.email_body}
              />
            </Grid>

            {/* Buttons */}
            <Grid
              item
              xs={12}
              display="flex"
              justifyContent="flex-end"
              gap={2}
              mt={2}
            >
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleReset}
              >
                Reset
              </Button>

              <Button
                variant="contained"
                color="primary"
                onClick={isEdit ? handleSubmit : handleSubmit}
              >
                {isEdit ? "Update" : "Add"}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default MeetingCard;
